// <s-purchase-options> switches between Bundle (Buy 1/2/3 sets the form quantity) and
// Subscribe (selling_plan radios, disabled while Bundle is active so a one-time
// purchase never carries a plan). State survives the product form's partial re-render
// on variant change via a per-product memory.
const memory = new Map();

class SPurchaseOptions extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('keydown', this.#onKeydown);
    this.quantity = this.form?.querySelector('[name="quantity"]');
    this.quantity?.addEventListener('change', this.#onQuantity);

    const saved = memory.get(this.dataset.productId) || {};
    if (saved.plan) {
      const plan = this.querySelector(`[name="selling_plan"][value="${CSS.escape(saved.plan)}"]`);
      if (plan) plan.checked = true;
    }
    if (saved.qty) {
      const bundle = this.querySelector(`[name="po_bundle"][value="${saved.qty}"]`);
      if (bundle) bundle.checked = true;
    }
    this.#select(saved.tab || this.dataset.start || 'bundle', false);
  }

  disconnectedCallback() {
    this.quantity?.removeEventListener('change', this.#onQuantity);
    this.watching?.disconnect();
  }

  #onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (tab) this.#select(tab.dataset.tab, true);
  };

  #onKeydown = (event) => {
    if (!event.target.matches('[role="tab"]') || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    const index = this.tabs.indexOf(event.target);
    const next = this.tabs[(index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length];
    next.focus();
    this.#select(next.dataset.tab, true);
  };

  #onChange = (event) => {
    if (event.target.name === 'po_bundle') this.#setQuantity(event.target.value);
    this.#remember();
  };

  #onQuantity = () => {
    const value = this.quantity.value;
    this.querySelectorAll('[name="po_bundle"]').forEach((radio) => {
      radio.checked = radio.value === value;
    });
    this.#remember();
  };

  #select(name, userAction) {
    const tab = this.tabs.find((t) => t.dataset.tab === name) || this.tabs[0];
    if (!tab) return;
    const active = tab.dataset.tab;
    this.tabs.forEach((t) => {
      t.setAttribute('aria-selected', String(t === tab));
      t.tabIndex = t === tab ? 0 : -1;
    });
    this.querySelectorAll('[data-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.panel !== active;
    });
    this.querySelectorAll('[name="selling_plan"]').forEach((radio) => {
      radio.disabled = active !== 'subscribe' || (this.#appWidget() !== null && radio.closest('[data-po-fallback]') !== null);
    });
    document.documentElement.dataset.po = active;
    this.#syncAppWidget(active);
    // Linked subscription product: swap which variant id the form submits.
    const subId = this.querySelector('[data-sub-id]');
    if (subId) {
      subId.disabled = active !== 'subscribe';
      this.form?.querySelectorAll('input[name="id"]:not([data-sub-id])').forEach((input) => {
        input.disabled = active === 'subscribe';
      });
    }
    if (active === 'bundle') {
      const bundle = this.querySelector('[name="po_bundle"]:checked') || this.querySelector('[name="po_bundle"]');
      if (bundle) {
        bundle.checked = true;
        if (userAction || !memory.has(this.dataset.productId)) this.#setQuantity(bundle.value);
      }
    }
    this.dataset.active = active;
    this.#remember();
  }


  // Appstle injects its own subscription widget near the add-to-cart button. When present it
  // owns subscriptions: we move it into the Subscribe panel (so it only shows on that tab),
  // select its subscription choice on Subscribe, and switch it back to one-time on Bundle.
  #appWidget() {
    const scope = this.closest('main') || document;
    const selector = '[class*="appstle"], [id*="appstle"]';
    const panel = this.querySelector('[data-panel="subscribe"]');
    const inPanel = panel?.querySelector('[data-po-app]');
    if (inPanel) return inPanel;
    const found = [...scope.querySelectorAll(selector)].find(
      (el) => !this.contains(el) && el.querySelector('input[type="radio"]') && !el.parentElement.closest(selector)
    );
    if (found) found.dataset.poApp = '';
    return found || null;
  }

  #syncAppWidget(active) {
    if (!this.watching) {
      this.watching = new MutationObserver(() => {
        const panel = this.querySelector('[data-panel="subscribe"]');
        const widget = this.#appWidget();
        if (widget && panel && !panel.contains(widget)) this.#select(this.dataset.active || 'bundle', false);
      });
      this.watching.observe(this.closest('main') || document.body, { childList: true, subtree: true });
    }
    const widget = this.#appWidget();
    if (!widget) return;
    const panel = this.querySelector('[data-panel="subscribe"]');
    if (panel && !panel.contains(widget)) panel.prepend(widget);
    const radios = [...widget.querySelectorAll('input[type="radio"]')];
    const isOneTime = (radio) => /one[-_ ]?time/i.test(radio.closest('[class*="one"], [id*="one"]')?.className || '') || radio.value === '';
    const oneTime = radios.find(isOneTime) || radios[0];
    const subscribe = radios.find((radio) => radio !== oneTime);
    const target = active === 'subscribe' ? subscribe : oneTime;
    if (target && !target.checked) target.click();
  }

  #setQuantity(value) {
    if (!this.quantity || this.quantity.value === value) return;
    this.quantity.value = value;
    this.quantity.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // The "Free gift" order note travels with Buy 2+, Buy 3 and subscription orders so the packing team knows to add one.
  #syncGift() {
    const gift = this.querySelector('[data-po-gift]');
    if (!gift) return;
    const quantity = Number.parseInt(this.quantity?.value || '1', 10);
    const qualifies = this.dataset.active === 'subscribe' || quantity >= 2;
    gift.disabled = !qualifies;
  }

  #remember() {
    this.#syncGift();
    memory.set(this.dataset.productId, {
      tab: this.dataset.active,
      qty: this.querySelector('[name="po_bundle"]:checked')?.value,
      plan: this.querySelector('[name="selling_plan"]:checked')?.value,
    });
  }
}

if (!customElements.get('s-purchase-options')) customElements.define('s-purchase-options', SPurchaseOptions);
